/**
 * contentValidators/interactivePayloadChecks.ts
 *
 * S-LEZ-04 / S-LEZ-05 / S-SOORT-04 / S-WOORD-03 — Interactive payload
 * coherence checks for shells that embed a non-trivial sub-mode in
 * `data.mode`. Three families of payloads are covered today:
 *
 *   - `text_highlight` (range-mode + `tap_referent` sub-mode) — the
 *     original S-LEZ-04 / S-LEZ-05 focus.
 *   - `text_highlight` with `mode: 'tap_word_class'` — S-SOORT-04
 *     follow-up after the TAA-SOORT Q2Q audit.
 *   - `multiple_choice` with `mode: 'two_step_judge'` — S-WOORD-03
 *     follow-up after the TAA-WOORD Q2Q audit. IDIOOM-007 uses an
 *     empty `options: []` and tucks the actual step1/step2 sub-options
 *     into `data`, so the schema-level multiple_choice validator never
 *     sees them. This sub-validator does.
 *
 * Catches the same families of failure modes for each sub-mode:
 *
 *   (a) text_highlight with `correctHighlights: [{start, end, text?}]`
 *       where the offsets do not actually match the `text` property
 *       (off-by-N copy-paste errors or sentence-overshoot). The
 *       TextHighlightRenderer is tolerant of small overlaps thanks to
 *       its `wordStart < h.end && wordEnd > h.start` check, but
 *       overshoot ranges silently include extra words in the "correct"
 *       set, leading to false negatives for didactically correct
 *       selections.
 *
 *   (b) text_highlight with `mode: 'tap_referent'` where the seed's
 *       `pronounIndex`, `correctIndex` or `candidateIndices` were
 *       counted manually and ended up off-by-one vs the renderer's
 *       actual `/\S+/g` whitespace tokenisation (TAA-LEZEN-VERW-008/009
 *       pre-fix).
 *
 *   (c) text_highlight with `mode: 'tap_word_class'` where the seed's
 *       `tappableWords` indices are out of range or missing the correct
 *       `isCorrect: true` flag.
 *
 *   (d) multiple_choice with `mode: 'two_step_judge'` where the step1
 *       or step2 options are missing, out of range, or the step2_ifIndex
 *       is invalid.
 *
 * Both checks reproduce the renderer's tokenisation (the renderer is the
 * source of truth — see TextHighlightRenderer.tsx `buildSegmentsForReferent`
 * and `buildSegmentsFromText`) so any drift between seed and renderer is
 * caught at governance time.
 *
 * Heuristic discipline (mirrors S2-01):
 *   - HARD findings (REVIEW, coach) only when a payload is provably
 *     broken in production: out-of-range indices, slice would crash,
 *     correct option not in candidate set.
 *   - SOFT findings (NEAR_LOW, info) for offset/text mismatches that
 *     currently survive renderer tolerance but indicate copy-paste
 *     errors authors should clean up.
 *   - Emits at most a small number of findings per question to avoid
 *     noise. Skips silently when payload shape is not the expected one.
 */

import type { ValidationIssue } from '../../issueContract'
import { coach, info } from '../../issueContract'
import type { Question } from '../../../../content'
import { STAGE } from './shared'
import { SEVERITY } from '../../severityScale'

// ─── Shared helpers ────────────────────────────────────────────────────

/**
 * Reproduce the renderer's whitespace tokenisation (TextHighlightRenderer
 * `/\S+/g`). Each token retains its trailing punctuation, exactly as the
 * renderer sees it. The returned indices are 0-based.
 */
function tokenizeLikeRenderer(text: string): string[] {
  if (!text) return []
  const tokens: string[] = []
  const re = /\S+/g
  let m: RegExpExecArray | null
  while ((m = re.exec(text)) !== null) {
    tokens.push(m[0])
  }
  return tokens
}

/**
 * Normalise a string for tolerant equality: lowercase, collapse
 * whitespace, strip surrounding punctuation/quotes. Used to compare a
 * seed's declared `text` property against the actual `passage.slice()`
 * — small trailing punctuation differences are common and harmless.
 */
function normaliseForCompare(s: string): string {
  return s
    .toLowerCase()
    .replace(/^[\s.,;:!?'"\u2018\u2019\u201C\u201D\u201E\-]+/, '')
    .replace(/[\s.,;:!?'"\u2018\u2019\u201C\u201D\u201E\-]+$/, '')
    .replace(/\s+/g, ' ')
    .trim()
}

// ─── Range-mode highlight offset checks ────────────────────────────────

interface RangeHighlight {
  start: number
  end: number
  text?: string
}

function isRangeHighlight(h: unknown): h is RangeHighlight {
  return (
    typeof h === 'object' &&
    h !== null &&
    'start' in h &&
    'end' in h &&
    typeof (h as { start: unknown }).start === 'number' &&
    typeof (h as { end: unknown }).end === 'number'
  )
}

function checkRangeHighlights(
  question: Question,
  text: string,
  highlights: unknown[],
): ValidationIssue[] {
  const issues: ValidationIssue[] = []
  const qId = question.id ?? 'UNKNOWN'
  const fId = question.familyId ?? 'UNKNOWN'

  for (let i = 0; i < highlights.length; i++) {
    const h = highlights[i]
    if (!isRangeHighlight(h)) continue // skip string-mode highlights; renderer handles them

    const { start, end } = h

    // ── HARD: out-of-range or inverted offsets ───────────────────────
    if (start < 0 || end > text.length || end <= start) {
      issues.push(
        coach(
          'content',
          STAGE,
          'CONTENT_INTERACTIVE_PAYLOAD',
          `text_highlight range #${i} is out of bounds or inverted: start=${start}, end=${end}, text.length=${text.length}.`,
          {
            field: `data.correctHighlights[${i}]`,
            expected: `0 ≤ start < end ≤ ${text.length}`,
            actual: `{ start: ${start}, end: ${end} }`,
            questionId: qId,
            familyId: fId,
            severity: SEVERITY.REVIEW,
            suggestion:
              'Recount the character offsets against the literal passage string (including spaces and punctuation). Ranges that overshoot the passage end will produce undefined behaviour in the renderer.',
            metadata: {
              reason: 'interactive_payload_invalid_range',
              subReason: 'range_out_of_bounds',
              start,
              end,
              textLength: text.length,
            },
          },
        ),
      )
      continue
    }

    // ── SOFT: declared `text` property does not match the slice ─────
    // Authors often hand-edit the slice indices without re-verifying the
    // `text` field. This is the off-by-N copy-paste signature.
    if (typeof h.text === 'string' && h.text.length > 0) {
      const actualSlice = text.slice(start, end)
      const declared = normaliseForCompare(h.text)
      const actual = normaliseForCompare(actualSlice)
      if (declared !== actual) {
        issues.push(
          info(
            'content',
            STAGE,
            'CONTENT_INTERACTIVE_PAYLOAD',
            `text_highlight range #${i} declared text does not match the slice — possible off-by-N offset.`,
            {
              field: `data.correctHighlights[${i}]`,
              expected: `text === passage.slice(start, end) (modulo trailing punctuation)`,
              actual: `declared="${h.text}"; slice="${actualSlice}"`,
              questionId: qId,
              familyId: fId,
              severity: SEVERITY.NEAR_LOW,
              suggestion:
                'Recount the offsets against the literal passage. The renderer tolerates small overlaps because it matches word-by-word, but overshoot ranges silently include extra words in the "correct" set, causing false negatives for didactically correct selections.',
              metadata: {
                reason: 'interactive_payload_offset_drift',
                subReason: 'declared_text_mismatch',
                declared: h.text,
                actualSlice,
                start,
                end,
              },
            },
          ),
        )
      }
    }
  }

  return issues
}

// ─── tap_referent index checks ─────────────────────────────────────────

function checkTapReferent(
  question: Question,
  passage: string,
  data: Record<string, unknown>,
): ValidationIssue[] {
  const issues: ValidationIssue[] = []
  const qId = question.id ?? 'UNKNOWN'
  const fId = question.familyId ?? 'UNKNOWN'

  const tokens = tokenizeLikeRenderer(passage)
  const N = tokens.length
  if (N === 0) return issues

  const pronounIndex = data.pronounIndex
  const correctIndex = data.correctIndex
  const candidateIndices = data.candidateIndices

  // ── HARD: indices missing or out-of-range ────────────────────────
  const isInt = (v: unknown): v is number =>
    typeof v === 'number' && Number.isInteger(v)
  const inRange = (i: number) => i >= 0 && i < N

  if (!isInt(pronounIndex) || !inRange(pronounIndex)) {
    issues.push(
      coach(
        'content',
        STAGE,
        'CONTENT_INTERACTIVE_PAYLOAD',
        `tap_referent pronounIndex is missing or out of range (got ${String(pronounIndex)}, tokens=${N}).`,
        {
          field: 'data.pronounIndex',
          expected: `integer in [0, ${N})`,
          actual: String(pronounIndex),
          questionId: qId,
          familyId: fId,
          severity: SEVERITY.REVIEW,
          suggestion: `Tokens (whitespace-split) for this passage: [${tokens.map((t, i) => `${i}:"${t}"`).join(', ')}]`,
          metadata: {
            reason: 'interactive_payload_invalid_index',
            subReason: 'pronoun_index_out_of_range',
            value: pronounIndex,
            tokenCount: N,
          },
        },
      ),
    )
  }

  if (!isInt(correctIndex) || !inRange(correctIndex)) {
    issues.push(
      coach(
        'content',
        STAGE,
        'CONTENT_INTERACTIVE_PAYLOAD',
        `tap_referent correctIndex is missing or out of range (got ${String(correctIndex)}, tokens=${N}).`,
        {
          field: 'data.correctIndex',
          expected: `integer in [0, ${N})`,
          actual: String(correctIndex),
          questionId: qId,
          familyId: fId,
          severity: SEVERITY.REVIEW,
          suggestion: `Tokens (whitespace-split) for this passage: [${tokens.map((t, i) => `${i}:"${t}"`).join(', ')}]`,
          metadata: {
            reason: 'interactive_payload_invalid_index',
            subReason: 'correct_index_out_of_range',
            value: correctIndex,
            tokenCount: N,
          },
        },
      ),
    )
  }

  const candidates = Array.isArray(candidateIndices) ? candidateIndices : null
  if (!candidates || candidates.length === 0) {
    issues.push(
      coach(
        'content',
        STAGE,
        'CONTENT_INTERACTIVE_PAYLOAD',
        `tap_referent candidateIndices is missing or empty.`,
        {
          field: 'data.candidateIndices',
          expected: 'non-empty array of integers in token range',
          actual: String(candidateIndices),
          questionId: qId,
          familyId: fId,
          severity: SEVERITY.REVIEW,
          suggestion:
            'Provide the indices of the words that should be tappable (including the correct referent).',
          metadata: {
            reason: 'interactive_payload_invalid_index',
            subReason: 'candidate_indices_missing',
          },
        },
      ),
    )
    return issues
  }

  const outOfRange: number[] = []
  for (const c of candidates) {
    if (!isInt(c) || !inRange(c)) outOfRange.push(c as number)
  }
  if (outOfRange.length > 0) {
    issues.push(
      coach(
        'content',
        STAGE,
        'CONTENT_INTERACTIVE_PAYLOAD',
        `tap_referent candidateIndices contains ${outOfRange.length} out-of-range value(s): [${outOfRange.join(', ')}] (tokens=${N}).`,
        {
          field: 'data.candidateIndices',
          expected: `all entries in [0, ${N})`,
          actual: JSON.stringify(candidates),
          questionId: qId,
          familyId: fId,
          severity: SEVERITY.REVIEW,
          suggestion: `Tokens (whitespace-split) for this passage: [${tokens.map((t, i) => `${i}:"${t}"`).join(', ')}]`,
          metadata: {
            reason: 'interactive_payload_invalid_index',
            subReason: 'candidate_indices_out_of_range',
            offending: outOfRange,
            tokenCount: N,
          },
        },
      ),
    )
  }

  // ── HARD: correctIndex must be one of the candidates ─────────────
  // Otherwise the correct token isn't tappable in the renderer → the
  // question is unanswerable.
  if (
    isInt(correctIndex) &&
    inRange(correctIndex) &&
    !candidates.includes(correctIndex)
  ) {
    issues.push(
      coach(
        'content',
        STAGE,
        'CONTENT_INTERACTIVE_PAYLOAD',
        `tap_referent correctIndex=${correctIndex} is not in candidateIndices=[${candidates.join(', ')}] — the correct token will not be tappable.`,
        {
          field: 'data.correctIndex',
          expected: 'correctIndex must be present in candidateIndices',
          actual: `correctIndex=${correctIndex}, candidateIndices=[${candidates.join(', ')}]`,
          questionId: qId,
          familyId: fId,
          severity: SEVERITY.REVIEW,
          suggestion:
            'Add the correct token index to candidateIndices, or correct the index by recounting tokens (whitespace-split, punctuation stays attached to the word).',
          metadata: {
            reason: 'interactive_payload_invalid_index',
            subReason: 'correct_not_in_candidates',
            correctIndex,
            candidateIndices: candidates,
          },
        },
      ),
    )
  }

  return issues
}

// ─── tap_word_class index checks ───────────────────────────────────────

/**
 * S-SOORT-04 — Validate `tap_word_class` mode payloads.
 *
 * Used by seeds like TAA-SOORT-BNW-007 where the learner taps every
 * word in a sentence that belongs to a target word class. Payload shape:
 *   data.sentence:      string
 *   data.tappableWords: Array<{ index: number; isCorrect: boolean }>
 *
 * The renderer tokenises `data.sentence` via `/\S+/g` (same convention as
 * `tap_referent`). Each `tappableWords[i].index` must fall inside that
 * token range. At least one entry must be `isCorrect: true`, otherwise
 * the question is unanswerable.
 */
function checkTapWordClass(
  question: Question,
  sentence: string,
  data: Record<string, unknown>,
): ValidationIssue[] {
  const issues: ValidationIssue[] = []
  const qId = question.id ?? 'UNKNOWN'
  const fId = question.familyId ?? 'UNKNOWN'

  const tokens = tokenizeLikeRenderer(sentence)
  const N = tokens.length
  if (N === 0) return issues

  const tappableWords = data.tappableWords
  if (!Array.isArray(tappableWords) || tappableWords.length === 0) {
    issues.push(
      coach(
        'content',
        STAGE,
        'CONTENT_INTERACTIVE_PAYLOAD',
        `tap_word_class tappableWords is missing or empty — question is unplayable.`,
        {
          field: 'data.tappableWords',
          expected: 'non-empty array of { index, isCorrect }',
          actual: String(tappableWords),
          questionId: qId,
          familyId: fId,
          severity: SEVERITY.REVIEW,
          suggestion:
            'Provide an entry for each tappable word with its token index and an isCorrect flag.',
          metadata: {
            reason: 'interactive_payload_invalid_payload',
            subReason: 'tappable_words_missing',
          },
        },
      ),
    )
    return issues
  }

  const isInt = (v: unknown): v is number =>
    typeof v === 'number' && Number.isInteger(v)
  const inRange = (i: number) => i >= 0 && i < N

  const outOfRange: Array<{ position: number; index: unknown }> = []
  let correctCount = 0

  for (let i = 0; i < tappableWords.length; i++) {
    const tw = tappableWords[i] as {
      index?: unknown
      isCorrect?: unknown
    } | null
    if (!tw || typeof tw !== 'object') continue

    const idx = tw.index
    if (!isInt(idx) || !inRange(idx)) {
      outOfRange.push({ position: i, index: idx })
    }
    if (tw.isCorrect === true) correctCount++
  }

  // ── HARD: any index out of range ─────────────────────────────────
  if (outOfRange.length > 0) {
    const detail = outOfRange
      .map((e) => `tappableWords[${e.position}].index=${String(e.index)}`)
      .join(', ')
    issues.push(
      coach(
        'content',
        STAGE,
        'CONTENT_INTERACTIVE_PAYLOAD',
        `tap_word_class has ${outOfRange.length} out-of-range index/indices: ${detail} (tokens=${N}).`,
        {
          field: 'data.tappableWords',
          expected: `every index in [0, ${N})`,
          actual: detail,
          questionId: qId,
          familyId: fId,
          severity: SEVERITY.REVIEW,
          suggestion: `Tokens (whitespace-split) for this sentence: [${tokens.map((t, i) => `${i}:"${t}"`).join(', ')}]`,
          metadata: {
            reason: 'interactive_payload_invalid_index',
            subReason: 'tap_word_class_index_out_of_range',
            offending: outOfRange,
            tokenCount: N,
          },
        },
      ),
    )
  }

  // ── HARD: no correct answer ──────────────────────────────────────
  if (correctCount === 0) {
    issues.push(
      coach(
        'content',
        STAGE,
        'CONTENT_INTERACTIVE_PAYLOAD',
        `tap_word_class has no tappableWords with isCorrect: true — question is unanswerable.`,
        {
          field: 'data.tappableWords',
          expected: 'at least one entry with isCorrect: true',
          actual: 'all entries have isCorrect: false (or missing)',
          questionId: qId,
          familyId: fId,
          severity: SEVERITY.REVIEW,
          suggestion:
            'Mark at least one tappable word as the correct target for the word class being learned.',
          metadata: {
            reason: 'interactive_payload_invalid_payload',
            subReason: 'tap_word_class_no_correct',
            entryCount: tappableWords.length,
          },
        },
      ),
    )
  }

  // ── SOFT: tappableWords count drifts from token count ────────────
  // Not a bug per se — authors may intentionally make only a subset
  // tappable — but a significant mismatch is worth flagging so authors
  // can confirm it's deliberate.
  if (tappableWords.length !== N) {
    issues.push(
      info(
        'content',
        STAGE,
        'CONTENT_INTERACTIVE_PAYLOAD',
        `tap_word_class tappableWords count (${tappableWords.length}) differs from token count (${N}). Verify this is intentional.`,
        {
          field: 'data.tappableWords',
          expected: `entry per tappable token (sentence has ${N} tokens)`,
          actual: `${tappableWords.length} entries`,
          questionId: qId,
          familyId: fId,
          severity: SEVERITY.NEAR_LOW,
          suggestion: `Tokens (whitespace-split) for this sentence: [${tokens.map((t, i) => `${i}:"${t}"`).join(', ')}]. If only some tokens should be tappable, ignore this. If all should be tappable, add the missing entries.`,
          metadata: {
            reason: 'interactive_payload_offset_drift',
            subReason: 'tap_word_class_count_mismatch',
            tappableCount: tappableWords.length,
            tokenCount: N,
          },
        },
      ),
    )
  }

  return issues
}

// ─── two_step_judge sub-mode checks ────────────────────────────────────

/**
 * S-WOORD-03 — Validate `multiple_choice` payloads that embed a
 * two-step judgement sub-mode (used by TAA-WOORD-IDIOOM-007).
 *
 * Payload shape:
 *   data.mode:           'two_step_judge'
 *   data.step1:          { prompt: string; options: string[]; correctIndex: number }
 *   data.step2_ifIndex?: number   // step1.options index that triggers step2
 *   data.step2?:         { prompt: string; options: string[]; correctIndex: number }
 *
 * The shell's top-level `options` is intentionally empty (`[]`) — all
 * answer surfaces live in step1/step2. That means the schema-level
 * multiple_choice validator can't catch out-of-range correctIndex
 * values or missing step structures. This sub-validator does.
 *
 * Discipline (mirrors S2-01 / S-SOORT-04):
 *   - HARD coach: step1 missing/empty, step1.correctIndex out of range,
 *     step2_ifIndex set but step2 missing or its correctIndex out of
 *     range. These all leave the question unplayable.
 *   - SOFT info: step2 present without a step2_ifIndex (dead-code
 *     branch — authors probably meant to wire it up), and step2_ifIndex
 *     itself out of range relative to step1.options.
 */
function checkTwoStepJudge(
  question: Question,
  data: Record<string, unknown>,
): ValidationIssue[] {
  const issues: ValidationIssue[] = []
  const qId = question.id ?? 'UNKNOWN'
  const fId = question.familyId ?? 'UNKNOWN'

  const isInt = (v: unknown): v is number =>
    typeof v === 'number' && Number.isInteger(v)

  // ── HARD: step1 must be a usable object with options + correctIndex ──
  const step1 = data.step1 as
    | { options?: unknown; correctIndex?: unknown; prompt?: unknown }
    | undefined
  if (
    !step1 ||
    typeof step1 !== 'object' ||
    !Array.isArray(step1.options) ||
    step1.options.length === 0
  ) {
    issues.push(
      coach(
        'content',
        STAGE,
        'CONTENT_INTERACTIVE_PAYLOAD',
        `two_step_judge step1 is missing or has empty options — question is unplayable.`,
        {
          field: 'data.step1',
          expected: '{ prompt, options: non-empty string[], correctIndex }',
          actual: JSON.stringify(step1 ?? null),
          questionId: qId,
          familyId: fId,
          severity: SEVERITY.REVIEW,
          suggestion:
            'Provide step1 with a non-empty options array and a correctIndex pointing at the right answer.',
          metadata: {
            reason: 'interactive_payload_invalid_payload',
            subReason: 'two_step_judge_step1_missing',
          },
        },
      ),
    )
    return issues
  }

  const step1N = step1.options.length
  const step1Correct = step1.correctIndex

  if (!isInt(step1Correct) || step1Correct < 0 || step1Correct >= step1N) {
    issues.push(
      coach(
        'content',
        STAGE,
        'CONTENT_INTERACTIVE_PAYLOAD',
        `two_step_judge step1.correctIndex is missing or out of range (got ${String(step1Correct)}, options=${step1N}).`,
        {
          field: 'data.step1.correctIndex',
          expected: `integer in [0, ${step1N})`,
          actual: String(step1Correct),
          questionId: qId,
          familyId: fId,
          severity: SEVERITY.REVIEW,
          suggestion: `step1.options: [${step1.options.map((o, i) => `${i}:"${String(o)}"`).join(', ')}]`,
          metadata: {
            reason: 'interactive_payload_invalid_index',
            subReason: 'two_step_judge_step1_correct_out_of_range',
            value: step1Correct,
            optionCount: step1N,
          },
        },
      ),
    )
  }

  // ── step2 chain — only relevant when step2_ifIndex is defined ────────
  const step2IfIndex = data.step2_ifIndex
  const step2 = data.step2 as
    | { options?: unknown; correctIndex?: unknown; prompt?: unknown }
    | undefined
  const step2IfDefined = step2IfIndex !== undefined && step2IfIndex !== null

  if (step2IfDefined) {
    // SOFT: step2_ifIndex must itself point at a valid step1 option.
    if (!isInt(step2IfIndex) || step2IfIndex < 0 || step2IfIndex >= step1N) {
      issues.push(
        info(
          'content',
          STAGE,
          'CONTENT_INTERACTIVE_PAYLOAD',
          `two_step_judge step2_ifIndex=${String(step2IfIndex)} is out of range for step1.options (length=${step1N}).`,
          {
            field: 'data.step2_ifIndex',
            expected: `integer in [0, ${step1N})`,
            actual: String(step2IfIndex),
            questionId: qId,
            familyId: fId,
            severity: SEVERITY.NEAR_LOW,
            suggestion:
              'Set step2_ifIndex to the index of the step1 option that should trigger step2 (typically the figurative/correct branch).',
            metadata: {
              reason: 'interactive_payload_invalid_index',
              subReason: 'two_step_judge_step2_if_out_of_range',
              value: step2IfIndex,
              optionCount: step1N,
            },
          },
        ),
      )
    }

    // HARD: step2 itself must be a usable object.
    if (
      !step2 ||
      typeof step2 !== 'object' ||
      !Array.isArray(step2.options) ||
      step2.options.length === 0
    ) {
      issues.push(
        coach(
          'content',
          STAGE,
          'CONTENT_INTERACTIVE_PAYLOAD',
          `two_step_judge step2_ifIndex is set but step2 is missing or has empty options — the second step is unreachable.`,
          {
            field: 'data.step2',
            expected: '{ prompt, options: non-empty string[], correctIndex }',
            actual: JSON.stringify(step2 ?? null),
            questionId: qId,
            familyId: fId,
            severity: SEVERITY.REVIEW,
            suggestion:
              'Either remove step2_ifIndex (single-step question), or provide a step2 object with options and correctIndex.',
            metadata: {
              reason: 'interactive_payload_invalid_payload',
              subReason: 'two_step_judge_step2_missing',
            },
          },
        ),
      )
      return issues
    }

    const step2N = step2.options.length
    const step2Correct = step2.correctIndex
    if (!isInt(step2Correct) || step2Correct < 0 || step2Correct >= step2N) {
      issues.push(
        coach(
          'content',
          STAGE,
          'CONTENT_INTERACTIVE_PAYLOAD',
          `two_step_judge step2.correctIndex is missing or out of range (got ${String(step2Correct)}, options=${step2N}).`,
          {
            field: 'data.step2.correctIndex',
            expected: `integer in [0, ${step2N})`,
            actual: String(step2Correct),
            questionId: qId,
            familyId: fId,
            severity: SEVERITY.REVIEW,
            suggestion: `step2.options: [${step2.options.map((o, i) => `${i}:"${String(o)}"`).join(', ')}]`,
            metadata: {
              reason: 'interactive_payload_invalid_index',
              subReason: 'two_step_judge_step2_correct_out_of_range',
              value: step2Correct,
              optionCount: step2N,
            },
          },
        ),
      )
    }
  } else if (step2 !== undefined) {
    // SOFT: step2 present without a step2_ifIndex is a dead-code branch.
    issues.push(
      info(
        'content',
        STAGE,
        'CONTENT_INTERACTIVE_PAYLOAD',
        `two_step_judge defines a step2 block but no step2_ifIndex — the second step will never be reached.`,
        {
          field: 'data.step2_ifIndex',
          expected: 'integer in step1.options range when step2 is present',
          actual: 'undefined',
          questionId: qId,
          familyId: fId,
          severity: SEVERITY.NEAR_LOW,
          suggestion:
            'Add step2_ifIndex (the step1 option index that should trigger step2), or remove the unreachable step2 block.',
          metadata: {
            reason: 'interactive_payload_invalid_payload',
            subReason: 'two_step_judge_step2_unreachable',
          },
        },
      ),
    )
  }

  return issues
}

// ─── Main entry point ──────────────────────────────────────────────────

/**
 * Validate interactive payload coherence for shells whose `data.mode`
 * embeds a sub-mode the schema-level validator can't see.
 *
 * Runs in four branches:
 *   - text_highlight + mode: 'tap_referent'   → tap_referent index checks
 *   - text_highlight + mode: 'tap_word_class' → tap_word_class index checks (S-SOORT-04)
 *   - multiple_choice + mode: 'two_step_judge' → two-step shell checks (S-WOORD-03)
 *   - text_highlight default                  → range offset checks
 *
 * Does nothing for question types and modes not covered above.
 */
export function validateInteractivePayload(
  question: Question,
): ValidationIssue[] {
  const data = (question as unknown as { data?: Record<string, unknown> }).data
  if (!data || typeof data !== 'object') return []

  const mode = data.mode

  // ── Branch: two_step_judge sub-mode (S-WOORD-03) ─────────────────
  // Lives inside multiple_choice shells, NOT text_highlight, so this
  // branch runs before the text_highlight early-return below.
  if (
    question.questionType === 'multiple_choice' &&
    mode === 'two_step_judge'
  ) {
    return checkTwoStepJudge(question, data)
  }

  if (question.questionType !== 'text_highlight') return []

  // ── Branch: tap_referent mode ────────────────────────────────────
  if (mode === 'tap_referent') {
    const passage = data.passage
    if (typeof passage !== 'string' || passage.length === 0) return []
    return checkTapReferent(question, passage, data)
  }

  // ── Branch: tap_word_class mode (S-SOORT-04) ─────────────────────
  if (mode === 'tap_word_class') {
    const sentence = data.sentence
    if (typeof sentence !== 'string' || sentence.length === 0) return []
    return checkTapWordClass(question, sentence, data)
  }

  // ── Branch: default range-mode text_highlight ────────────────────
  const text = data.text
  const highlights = data.correctHighlights
  if (typeof text !== 'string' || text.length === 0) return []
  if (!Array.isArray(highlights) || highlights.length === 0) return []

  return checkRangeHighlights(question, text, highlights)
}
